import { BehaviorSubject, combineLatest, filter, of, switchMap, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { DesktopInventoryLocationsTableComponent } from '@inventory/components/desktop-inventory-locations-table/desktop-inventory-locations-table.component';
import { InventoryItemsByLocationOffcanvasComponent } from '@inventory/components/inventory-items-by-location-offcanvas/inventory-items-by-location-offcanvas.component';
import { InventoryLocationCreateOffcanvasComponent } from '@inventory/components/inventory-location-create-offcanvas/inventory-location-create-offcanvas.component';
import { InventoryLocationDetailsOffcanvasComponent } from '@inventory/components/inventory-location-details-offcanvas/inventory-location-details-offcanvas.component';
import { MobileInventoryLocationsTableComponent } from '@inventory/components/mobile-inventory-locations-table/mobile-inventory-locations-table.component';
import { IInventoryLocation } from '@inventory/models/inventory';
import { InventoryLocationsService } from '@inventory/services/inventory-locations/inventory-locations.service';
import { LayoutService } from '@layout/services/layout/layout.service';
import { IOrganisationAndOrganisationUser, OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { UnauthorisedBannerComponent } from '@shared/components/ui/unauthorised-banner/unauthorised-banner.component';
import { ToastTypes } from '@shared/models/toast';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-inventory-locations',
	imports: [
		CommonModule,
		LoadingStateComponent,
		UnauthorisedBannerComponent,
		DesktopInventoryLocationsTableComponent,
		MobileInventoryLocationsTableComponent,
	],
	templateUrl: './inventory-locations.component.html',
	styleUrl: './inventory-locations.component.scss',
})
export class InventoryLocationsComponent implements OnInit, OnDestroy {
	destroyed$ = new EventEmitter<void>();
	private inventoryLocationService = inject(InventoryLocationsService);
	private organisationSelectedService = inject(OrganisationSelectedService);
	private layoutService = inject(LayoutService);
	private offcanvasService = inject(OffcanvasService);
	private toastService = inject(ToastService);

	selectedOrganisation: IOrganisationAndOrganisationUser | null;

	isLoading = true;
	locationsLoading = true;
	isMobileSize = true;
	userHasAccess = false;

	inventoryLocations: Array<IInventoryLocation> = [];
	showArchived$ = new BehaviorSubject<boolean>(false);

	ngOnInit(): void {
		this.layoutService.isMobileSize$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
			this.isMobileSize = value;
		});

		combineLatest([this.organisationSelectedService.selectedOrganisation, this.showArchived$])
			.pipe(
				takeUntil(this.destroyed$),
				filter(([selectedOrganisation]) => !!selectedOrganisation),
				switchMap(([selectedOrganisation, showArchived]) => {
					this.selectedOrganisation = selectedOrganisation;
					if (
						selectedOrganisation &&
						[OrganisationUserAccessLevel.OWNER, OrganisationUserAccessLevel.ADMIN].includes(
							selectedOrganisation.organisationUser.accessLevel,
						)
					) {
						this.userHasAccess = true;
						this.locationsLoading = true;

						return this.inventoryLocationService.getInventoryLocationsByOrganisation(
							selectedOrganisation.organisationUser.organisation,
							showArchived,
						);
					} else {
						this.userHasAccess = false;
						this.isLoading = false;
						return of([]);
					}
				}),
			)
			.subscribe((inventoryLocations) => {
				this.inventoryLocations = inventoryLocations;
				this.isLoading = false;
				this.locationsLoading = false;
			});
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	onInventoryLocationEdit(inventoryLocation: IInventoryLocation) {
		const ref = this.offcanvasService.open(InventoryLocationDetailsOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as InventoryLocationDetailsOffcanvasComponent;
		componentInstance.inventoryLocation = inventoryLocation;
	}

	onInventoryLocationArchive(inventoryLocation: IInventoryLocation) {
		this.inventoryLocationService
			.archiveInventoryLocation(inventoryLocation.id as string)
			.then(() => {
				this.toastService.showToast({
					title: 'Inventory Location',
					message: `Successfully archived inventory location.`,
					type: ToastTypes.SUCCESS,
				});
			})
			.catch((error) => {
				console.error('Error archiving inventory location:', error);
				this.toastService.showToast({
					title: 'Inventory Location',
					message: `Failed to archive inventory location: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}

	onInventoryLocationUnarchive(inventoryLocation: IInventoryLocation) {
		this.inventoryLocationService
			.unArchiveInventoryLocation(inventoryLocation.id as string)
			.then(() => {
				this.toastService.showToast({
					title: 'Inventory Location',
					message: `Successfully unarchived inventory location.`,
					type: ToastTypes.SUCCESS,
				});
			})
			.catch((error) => {
				console.error('Error unarchiving inventory location:', error);
				this.toastService.showToast({
					title: 'Inventory Location',
					message: `Failed to unarchive inventory location: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}

	openAddInventoryLocationOffcanvas() {
		const ref = this.offcanvasService.open(InventoryLocationCreateOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as InventoryLocationCreateOffcanvasComponent;
		componentInstance.organisationRef = this.selectedOrganisation!.organisationUser.organisation;
	}

	openViewInventoryItemsOffcanvas(inventoryLocation: IInventoryLocation) {
		const ref = this.offcanvasService.open(InventoryItemsByLocationOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as InventoryItemsByLocationOffcanvasComponent;
		componentInstance.inventoryLocation = inventoryLocation;
	}
}
